import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { devWarn } from '../lib/logger';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip the auth probe entirely for public routes — these pages don't use
    // `useAuth()` and a credentialed cross-origin call here can hang or error
    // in mobile in-app browsers (WhatsApp, Instagram, FB Messenger), making
    // the page appear blank.
    if (typeof window !== 'undefined') {
      const p = window.location.pathname || '';
      const PUBLIC_PREFIXES = ['/shared/', '/shared-mobile/', '/flipbook/'];
      if (PUBLIC_PREFIXES.some(prefix => p.startsWith(prefix))) {
        setUser(null);
        setLoading(false);
        return;
      }
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setUser(data);
      return { success: true, must_change_password: data.must_change_password };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { devWarn('Logout request failed (continuing client-side):', err); }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
